/**
 * This is a wrapper around the axios library. It creates an axios instance and appends headers based on requests.
 */
import axios from "axios";
import { getToken } from "../utils/auth";

const apiClient = axios.create({
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Track active requests for loading state
let activeRequests = 0;
let loadingCallbacks: Array<(isLoading: boolean) => void> = [];

// Function to register loading callbacks
export const registerLoadingCallback = (callback: (isLoading: boolean) => void): (() => void) => {
  loadingCallbacks.push(callback);
  return () => {
    loadingCallbacks = loadingCallbacks.filter((cb) => cb !== callback);
  };
};

// Notify all registered callbacks about loading state change
const notifyLoadingStateChange = (isLoading: boolean): void => {
  loadingCallbacks.forEach((callback) => callback(isLoading));
};

apiClient.interceptors.request.use(
  (config) => {
    activeRequests++;
    if (activeRequests === 1) {
      notifyLoadingStateChange(true);
    }

    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    activeRequests = Math.max(0, activeRequests - 1);
    if (activeRequests === 0) {
      notifyLoadingStateChange(false);
    }
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response) => {
    activeRequests = Math.max(0, activeRequests - 1);
    if (activeRequests === 0) {
      notifyLoadingStateChange(false);
    }
    return response;
  },
  (error) => {
    activeRequests = Math.max(0, activeRequests - 1);
    if (activeRequests === 0) {
      notifyLoadingStateChange(false);
    }
    return Promise.reject(error);
  }
);

export default apiClient;
